// PHASE_0 B0 deliverable 2 — keyset-paged, virtualization-ready data table.
//
// The single canonical list/table surface every P1+ screen reuses ([D6]).
// Two design constraints from BUILD_PLAN §5:
//
//   * Keyset (seek) pagination, never OFFSET. The table is page-shape
//     agnostic — it renders the rows it's handed and exposes a "load more"
//     affordance wired to the host's keyset hook (apps/customer-web/src/api/
//     keyset.ts → useKeysetQuery). It owns no fetching.
//
//   * Virtualization-ready windowing seam (BUILD_PLAN §5 "Org chart render"
//     / "List render"). The `renderWindow` prop is the fully-custom seam
//     (the task inbox passes its own hardened renderer). Faz 5 adds the
//     built-in `virtualize` mode on top of the same seam: above a row-count
//     threshold the body mounts only the visible slice + overscan inside a
//     fixed-height scroll region, using top/bottom spacer <tr>s so the
//     markup stays a valid, column-aligned <table> (a div-positioning
//     engine like react-window cannot express windowed <tbody> rows). At or
//     below the threshold the table renders every row exactly as before.
//
// Columns are declarative; cells are caller-rendered so money goes through
// <Money> and any localized text stays an i18n key at the call site — the
// table never formats values itself.

import { forwardRef, useCallback, useState, type CSSProperties, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";
import {
  clampDataTableScrollTop,
  DATA_TABLE_HEADER_HEIGHT,
  DATA_TABLE_MIN_INLINE_SIZE,
  DATA_TABLE_OVERSCAN,
  DATA_TABLE_ROW_HEIGHT,
  DATA_TABLE_VIEWPORT_HEIGHT,
  DATA_TABLE_WINDOW_THRESHOLD,
  getDataTableWindow,
  type DataTableVirtualizeOptions,
} from "./window.ts";

export interface DataTableColumn<TRow> {
  /** Stable column id — also the React key for header/cells. */
  readonly id: string;
  /** Already-localized header content (host passes t("...")). */
  readonly header: ReactNode;
  /** Cell renderer for this column. */
  readonly cell: (row: TRow) => ReactNode;
  /** Optional header scope/alignment hook for tenant CSS. */
  readonly align?: "start" | "center" | "end";
}

/**
 * Windowing seam. P0 default renders all rows; a P1 caller can pass a
 * windowing renderer that returns only the visible subset's <tr>s without
 * altering the table contract. The renderer receives the full row set + the
 * per-row renderer the table would otherwise call directly.
 */
export type DataTableWindow<TRow> = (args: {
  readonly rows: readonly TRow[];
  readonly renderRow: (row: TRow, index: number) => ReactNode;
}) => ReactNode;

/**
 * Gives a row a real address (SEM-6348 A2). Before this, three solutions were
 * in circulation across the app: `<tr onClick={navigate(...)}>` (produces no
 * href, so right-click → open in new tab, middle-click and "copy link address"
 * all fail), a hand-written link inside one cell, or plain text.
 *
 * One object rather than separate `rowHref` / `renderRowLink` props: the two
 * halves are useless apart. A caller that supplied only the address would get
 * a silently dead link, and this package deliberately carries NO router
 * dependency (peers are react/react-dom only) — a plain <a href> would
 * full-page-reload the SPA, which is worse than the navigate() it replaces.
 * So the app passes its own router Link here.
 */
export interface DataTableRowLink<TRow> {
  /** The row's address, or `undefined` when this row has none yet. */
  readonly href: (row: TRow) => string | undefined;
  /** Renders the anchor. The app supplies its router's Link. */
  readonly render: (args: {
    readonly href: string;
    readonly children: ReactNode;
  }) => ReactNode;
  /**
   * Column whose cell carries the anchor; defaults to the first column.
   * The cell is wrapped rather than the whole row: most of these lists end in
   * an actions column, and a row-wide overlay would sit on top of its buttons.
   */
  readonly columnId?: string;
}

export interface DataTableProps<TRow> {
  readonly columns: readonly DataTableColumn<TRow>[];
  readonly rows: readonly TRow[];
  /** Stable row key extractor. */
  readonly rowKey: (row: TRow) => string;
  /** Accessible caption / table label (already localized). */
  readonly caption: string;
  /**
   * Total accessible row count, including the header row. Virtualized callers
   * pass -1 when the result is still an open-ended keyset stream.
   */
  readonly ariaRowCount?: number;
  /** Whether more keyset pages exist (from useKeysetQuery.hasNextPage). */
  readonly hasNextPage?: boolean;
  /** Load-the-next-keyset-page handler (useKeysetQuery.fetchNextPage). */
  readonly onLoadMore?: () => void;
  /** True while the next page is in flight — disables the load-more control. */
  readonly isFetchingNextPage?: boolean;
  /** Already-localized label for the load-more control. */
  readonly loadMoreLabel?: string;
  /**
   * Optional windowing renderer (virtualization seam). When omitted the
   * table renders every row — correct for P0 + small lists.
   */
  readonly renderWindow?: DataTableWindow<TRow>;
  /**
   * Built-in windowed mode (Faz 5). `true` uses the shared geometry defaults;
   * an options object tunes them. Above the row threshold the table body
   * mounts only the visible slice + overscan inside a fixed-height scroll
   * region (sticky header, spacer rows preserve scrollbar geometry + valid
   * <table> markup). At or below the threshold this is a strict no-op — the
   * DOM is byte-identical to today's. Mutually exclusive with `renderWindow`
   * (a custom renderer wins).
   */
  readonly virtualize?: boolean | DataTableVirtualizeOptions;
  /** Optional per-row address. See {@link DataTableRowLink}. */
  readonly rowLink?: DataTableRowLink<TRow>;
  readonly className?: string;
}

type DataTableViewportStyle = CSSProperties & {
  readonly "--nai-data-table-header-height": string;
  readonly "--nai-data-table-min-inline-size": string;
  readonly "--nai-data-table-row-height": string;
  readonly "--nai-data-table-viewport-height": string;
};

/**
 * Zero-chrome scroll-space reservation row. Spacer rows keep the windowed
 * body a valid <tbody> (only <tr> children) while the browser scrollbar
 * still reflects the FULL row count.
 */
function DataTableSpacerRow({
  height,
  colSpan,
}: {
  readonly height: number;
  readonly colSpan: number;
}): React.ReactElement {
  return (
    <tr data-slot="data-table-spacer" aria-hidden="true">
      <td colSpan={colSpan} style={{ border: 0, height: 0, lineHeight: 0, padding: 0 }}>
        <div style={{ height }} />
      </td>
    </tr>
  );
}

function DataTableInner<TRow>(
  props: DataTableProps<TRow>,
  ref: React.ForwardedRef<HTMLTableElement>,
): React.ReactElement {
  const {
    columns,
    rows,
    rowKey,
    caption,
    ariaRowCount,
    hasNextPage = false,
    onLoadMore,
    isFetchingNextPage = false,
    loadMoreLabel,
    renderWindow,
    virtualize,
    rowLink,
    className,
  } = props;

  const linkColumnId = rowLink?.columnId ?? columns[0]?.id;

  const virtualizeOptions: DataTableVirtualizeOptions | undefined =
    virtualize === undefined || virtualize === false
      ? undefined
      : virtualize === true
        ? {}
        : virtualize;
  const rowHeight = virtualizeOptions?.rowHeight ?? DATA_TABLE_ROW_HEIGHT;
  const viewportHeight = virtualizeOptions?.viewportHeight ?? DATA_TABLE_VIEWPORT_HEIGHT;
  const threshold = virtualizeOptions?.threshold ?? DATA_TABLE_WINDOW_THRESHOLD;
  const overscan = virtualizeOptions?.overscan ?? DATA_TABLE_OVERSCAN;
  // A caller-supplied renderWindow keeps full control of the seam; the
  // built-in mode only engages above the threshold so small tables keep
  // today's exact DOM and semantics.
  const windowed =
    renderWindow === undefined && virtualizeOptions !== undefined && rows.length > threshold;

  const [scrollTop, setScrollTop] = useState(0);
  // The viewport <div> is only mounted while `windowed`, so toggling it (a
  // filter dipping the row count below the threshold and back) remounts a
  // fresh node at native scrollTop 0 while this state survives. Re-sync
  // during render (React's adjust-state-on-prop-change pattern) so the slice
  // can never be computed from a scroll offset the new viewport doesn't have.
  const [prevWindowed, setPrevWindowed] = useState(windowed);
  if (prevWindowed !== windowed) {
    setPrevWindowed(windowed);
    if (scrollTop !== 0) setScrollTop(0);
  }

  // Windowed bodies omit rows from the DOM, so assistive tech needs the
  // absolute row indices even when the host didn't opt in via ariaRowCount.
  const effectiveAriaRowCount =
    ariaRowCount ?? (windowed ? (hasNextPage ? -1 : rows.length + 1) : undefined);

  const onViewportScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>): void => {
      const viewport = event.currentTarget;
      const geometry = {
        rowCount: rows.length,
        scrollTop: viewport.scrollTop,
        rowHeight,
        viewportHeight,
        overscan,
      };
      const clamped = clampDataTableScrollTop(geometry);
      const nextSlice = getDataTableWindow({ ...geometry, scrollTop: clamped });
      // A wheel/scrollbar move can unmount the focused control and drop the
      // browser's active element to <body>. Keep the labeled scroll region
      // focused before React replaces the virtual slice.
      const active = document.activeElement;
      if (active instanceof HTMLElement && viewport.contains(active)) {
        const activeRow = active.closest<HTMLTableRowElement>('tr[data-slot="data-table-row"]');
        const activeIndex = activeRow === null ? Number.NaN : Number(activeRow.dataset.rowIndex);
        if (
          Number.isInteger(activeIndex) &&
          (activeIndex < nextSlice.startIndex || activeIndex >= nextSlice.endIndex)
        ) {
          viewport.focus();
        }
      }
      if (viewport.scrollTop !== clamped) viewport.scrollTop = clamped;
      setScrollTop((current) => (current === clamped ? current : clamped));
    },
    [overscan, rowHeight, rows.length, viewportHeight],
  );

  const renderCell = (col: DataTableColumn<TRow>, row: TRow): ReactNode => {
    const content = col.cell(row);
    if (rowLink === undefined || col.id !== linkColumnId) return content;
    const href = rowLink.href(row);
    // An `href=""` resolves to the current page: it looks like a link and
    // does nothing. A row without an address stays text.
    if (href === undefined || href === "") return content;
    return rowLink.render({ href, children: content });
  };

  const renderRow = (row: TRow, index: number): ReactNode => (
    <tr
      key={rowKey(row)}
      data-slot="data-table-row"
      data-row-index={effectiveAriaRowCount === undefined ? undefined : index}
      aria-rowindex={effectiveAriaRowCount === undefined ? undefined : index + 2}
    >
      {columns.map((col) => (
        <td key={col.id} data-align={col.align ?? "start"}>
          {renderCell(col, row)}
        </td>
      ))}
    </tr>
  );

  const renderBody = (): ReactNode => {
    if (renderWindow !== undefined) return renderWindow({ rows, renderRow });
    if (!windowed) return rows.map((row, i) => renderRow(row, i));
    const slice = getDataTableWindow({
      rowCount: rows.length,
      scrollTop,
      rowHeight,
      viewportHeight,
      overscan,
    });
    return (
      <>
        {slice.topSpacerHeight > 0 && (
          <DataTableSpacerRow height={slice.topSpacerHeight} colSpan={columns.length} />
        )}
        {rows
          .slice(slice.startIndex, slice.endIndex)
          .map((row, i) => renderRow(row, slice.startIndex + i))}
        {slice.bottomSpacerHeight > 0 && (
          <DataTableSpacerRow height={slice.bottomSpacerHeight} colSpan={columns.length} />
        )}
      </>
    );
  };

  const table = (
    <table ref={ref} className="nai-data-table-table" aria-rowcount={effectiveAriaRowCount}>
      <caption className="nai-sr-only">{caption}</caption>
      <thead>
        <tr aria-rowindex={effectiveAriaRowCount === undefined ? undefined : 1}>
          {columns.map((col) => (
            <th key={col.id} scope="col" data-align={col.align ?? "start"}>
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{renderBody()}</tbody>
    </table>
  );

  const viewportStyle: DataTableViewportStyle = {
    "--nai-data-table-header-height": `${DATA_TABLE_HEADER_HEIGHT}px`,
    "--nai-data-table-min-inline-size": `${DATA_TABLE_MIN_INLINE_SIZE}px`,
    "--nai-data-table-row-height": `${rowHeight}px`,
    "--nai-data-table-viewport-height": `${viewportHeight}px`,
  };

  return (
    <div data-component="data-table" className={cx("nai-data-table", className)}>
      {windowed ? (
        <div
          data-slot="data-table-viewport"
          role="region"
          aria-label={caption}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a labeled overflow region needs a keyboard scroll/focus fallback target
          tabIndex={0}
          className="nai-data-table-viewport"
          style={viewportStyle}
          onScroll={onViewportScroll}
        >
          {table}
        </div>
      ) : (
        // Yatay kaydırma kapsayıcısı (SEM-4928). Bir tablo sütun sayısı kadar
        // geniştir ve sıkışamaz; kapsayıcısı yoksa taşmayı SAYFAYA devreder ve
        // tabloyla ilgisi olmayan başlık, menü, kart hepsi birlikte kayar.
        // Pencerelenmiş kolun kendi görüntü alanı zaten kaydırıyor, o yüzden
        // yalnız bu dal sarmalanıyor.
        // `role="region"` bilerek YOK: çağıran yüzeylerin bir kısmı tabloyu
        // zaten kendi etiketli bölgesine sarıyor (ör. görev tablosu) ve aynı adı
        // taşıyan ikinci bir yer işareti axe `landmark-unique` ihlali üretiyor.
        // Klavye erişimi için yalnız odaklanabilirlik gerekir.
        <div
          data-slot="data-table-scroll"
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- an overflow container needs a keyboard scroll target
          tabIndex={0}
          className="nai-data-table-scroll"
        >
          {table}
        </div>
      )}

      {hasNextPage && onLoadMore !== undefined && (
        <div data-slot="data-table-load-more">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            aria-busy={isFetchingNextPage || undefined}
            className="nai-data-table-load-more"
          >
            {loadMoreLabel}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Generic forwardRef wrapper. Cast preserves the `<TRow>` generic through
 * forwardRef (which otherwise erases it) so call sites keep full row typing.
 */
export const DataTable = forwardRef(DataTableInner) as <TRow>(
  props: DataTableProps<TRow> & { ref?: React.ForwardedRef<HTMLTableElement> },
) => React.ReactElement;
